import { useState } from 'react';
import { Box, SimpleGrid, Stack, Group, Badge, TextInput, Select } from '@mantine/core';
import {
  IconSearch,
  IconPlus,
  IconEye,
  IconThumbUp,
  IconRocket,
  IconUser,
  IconReceipt,
  IconCode,
  IconDatabase,
  IconTool,
} from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockKnowledgeArticles, knowledgeCategories, KnowledgeArticle } from '../mockData/helpdesk';

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-US').format(value);
};

const getCategoryIcon = (icon: string) => {
  switch (icon) {
    case 'rocket': return IconRocket;
    case 'user': return IconUser;
    case 'receipt': return IconReceipt;
    case 'code': return IconCode;
    case 'database': return IconDatabase;
    case 'tool': return IconTool;
    default: return IconRocket;
  }
};

const getStatusColor = (status: KnowledgeArticle['status']) => {
  switch (status) {
    case 'published': return 'green';
    case 'draft': return 'yellow';
    case 'archived': return 'gray';
    default: return 'gray';
  }
};

export default function KnowledgeBasePage() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const categories = [...new Set(mockKnowledgeArticles.map(a => a.category))];

  const filteredArticles = mockKnowledgeArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(search.toLowerCase()) ||
                          article.tags.some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = !categoryFilter || article.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Sort by view count descending
  const sortedArticles = [...filteredArticles].sort((a, b) => b.viewCount - a.viewCount);

  const totalViews = mockKnowledgeArticles.reduce((acc, a) => acc + a.viewCount, 0);
  const totalHelpful = mockKnowledgeArticles.reduce((acc, a) => acc + a.helpfulCount, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Knowledge Base</Title>
          <Text c="dimmed" size="sm">{mockKnowledgeArticles.length} articles</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>New Article</Button>
      </Group>

      {/* Stats */}
      <Group>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Total Articles</Text>
          <Text size="xl" fw={700}>{mockKnowledgeArticles.length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Total Views</Text>
          <Text size="xl" fw={700}>{formatNumber(totalViews)}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Helpful Votes</Text>
          <Text size="xl" fw={700}>{formatNumber(totalHelpful)}</Text>
        </Card>
      </Group>

      {/* Categories */}
      <Card>
        <Text fw={600} mb="md">Categories</Text>
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }}>
          {knowledgeCategories.map(category => {
            const Icon = getCategoryIcon(category.icon);
            return (
              <Box
                key={category.name}
                p="md"
                onClick={() => setCategoryFilter(categoryFilter === category.name ? null : category.name)}
                style={{
                  backgroundColor: categoryFilter === category.name
                    ? 'var(--mantine-color-blue-light)'
                    : 'var(--mantine-color-gray-light)',
                  borderRadius: 8,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <Icon size={24} color="var(--mantine-color-blue-filled)" />
                <Text size="sm" fw={500} mt="xs">{category.name}</Text>
                <Text size="xs" c="dimmed">{category.count} articles</Text>
              </Box>
            );
          })}
        </SimpleGrid>
      </Card>

      {/* Search */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search articles..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Category"
            data={categories.map(c => ({ value: c, label: c }))}
            value={categoryFilter}
            onChange={setCategoryFilter}
            clearable
            w={200}
          />
        </Group>
      </Card>

      {/* Articles */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {sortedArticles.map(article => (
          <Card key={article.id}>
            <Stack gap="md">
              <Group justify="space-between">
                <Badge size="sm" variant="light">{article.category}</Badge>
                <Badge size="sm" color={getStatusColor(article.status)}>{article.status}</Badge>
              </Group>

              <Box>
                <Text fw={600} lineClamp={2}>{article.title}</Text>
                <Text size="xs" c="dimmed" mt="xs">by {article.authorName}</Text>
              </Box>

              <Group gap="xs">
                {article.tags.slice(0, 3).map(tag => (
                  <Badge key={tag} size="xs" variant="outline">{tag}</Badge>
                ))}
              </Group>

              <Group justify="space-between">
                <Group gap="md">
                  <Group gap={4}>
                    <IconEye size={14} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">{formatNumber(article.viewCount)}</Text>
                  </Group>
                  <Group gap={4}>
                    <IconThumbUp size={14} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">{formatNumber(article.helpfulCount)}</Text>
                  </Group>
                </Group>
                <Text size="xs" c="dimmed">{formatDate(article.updatedAt)}</Text>
              </Group>

              <Button variant="light" size="xs" fullWidth>Read Article</Button>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
